/**
 * round-4 FINDING 1 — resolve the export-time position of an INSERT's ATTRIB
 * child when its `overridePosition` is unset.
 *
 * Split out of `dxfBridge.entityConversion.ts` to keep that file under the
 * CLAUDE.md 2000-line guardrail. Owns the whole ATTRIB export encoder
 * (`drawingAttribToDxfAttrib`, relocated here round-6 for the same guardrail)
 * plus its placement resolver (`resolveAttribExportGeometry`); the other
 * exports are their helpers. `dxfBridge.entityConversion.ts` re-exports
 * `drawingAttribToDxfAttrib` (preserving the barrel surface) and is its only
 * caller, passing the `baseCommon` it builds via `toDxfCommonProps`.
 */

import type { DxfAttribEntity } from "@dxf-ai-editor/dxf-io";

import { type AttdefEntity, type BlockDefinition, type BlockInsertAttribute, type BlockInsertEntity, type DrawingPoint } from "./model";

/**
 * round-4 FINDING 1 — export-side twin of `attdefFromEntity.ts`'s
 * `usesAlignmentPoint`/`resolveTemplateAnchor` (scene3d render). DXF
 * horizontal justification (group 72) / vertical (group 74) nonzero means the
 * ATTDEF's visual anchor is the alignment point (group 11/21), not the
 * insertion point (10/20) — Autodesk DXF reference, TEXT/ATTDEF-shared rule.
 * Duplicated here (not imported) because `dxfBridge` lives in `drawing-model`
 * and `attdefFromEntity` lives in the scene3d render adapter (apps/web) — the
 * reverse import direction is a package-boundary violation. A 2-line pure
 * predicate duplicated across an unrelated package boundary already has
 * precedent in this exact codebase (`attdefFromEntity.ts usesAlignmentPoint`'s
 * own header calls out the identical PDF/scene3d duplication as "incidental,
 * not a semantic clone to extract").
 */
export function usesAlignmentPointForExport(hJust: number, vJust: number): boolean {
  return hJust !== 0 || vJust !== 0;
}

/**
 * round-4 FINDING 1 — resolve an ATTDEF template's OWN block-local anchor for
 * export: `alignmentPoint` (11/21) for a justified template that carries one,
 * `insertionPoint` (10/20) otherwise. Export-side twin of
 * `attdefFromEntity.ts resolveTemplateAnchor` (render).
 */
export function resolveAttdefTemplateAnchorForExport(attdef: AttdefEntity): DrawingPoint {
  return usesAlignmentPointForExport(attdef.hJustification, attdef.vJustification) &&
    attdef.alignmentPoint !== undefined
    ? attdef.alignmentPoint
    : attdef.insertionPoint;
}

/**
 * round-4 FINDING 1 — transform a block-local point to WORLD/OCS space using
 * the parent INSERT's own translate/rotate/scale, for the ONE fallback this
 * encoder needs (resolve an unset ATTRIB `overridePosition` to the ATTDEF
 * template's placed position). Mirrors
 * `apps/web/src/app/scene3d/adapters/typed/blockTransform.ts
 * transformBlockChildPoint` EXACTLY (same subtract-basePoint / scale /
 * rotate / translate order, same CCW-positive-degrees sign convention — DXF
 * INSERT group 50 is documented CCW-positive, and `blockTransform.ts`'s own
 * golden parity test locks the identical formula: block-local `(0,0)` −
 * basePoint `(2,1)` rotated 90° → `(1,−2)`, translated by insertion `(3,4)`
 * → `(4,2)` — `block-flatten-parity.test.ts` "non-zero block basePoint is
 * subtracted before the transform"). Duplicated here (not imported) because
 * `drawing-model` cannot depend on the scene3d render adapter (apps/web) —
 * reverse package-boundary violation; a small pure 2D-point formula
 * duplicated across an unrelated package boundary already has precedent in
 * this exact codebase (`usesAlignmentPointForExport` above,
 * `blockMutations.ts`'s own EXPLODE math being a second independent copy of
 * this SAME family of transform).
 *
 * NOTE: `command-engine/src/blockMutations.ts composeInsertMatrix` (EXPLODE)
 * negates `rotateDeg` before building its rotation matrix — a DIFFERENT,
 * untested-with-rotation convention (no `rotation !== 0` case exists in
 * `explodeInsertMutation`'s own test suite) that does NOT match the verified
 * render-path formula above. This encoder intentionally does NOT mirror that
 * negation — it follows the render path's own DXF-conformant convention
 * instead, confirmed against `ezdxf` ground truth (`add_auto_attribs`) during
 * implementation.
 */
export function transformAttdefAnchorForExport(
  point: { x: number; y: number },
  basePoint: { x: number; y: number },
  insertion: { x: number; y: number },
  rotationDeg: number,
  scaleX: number,
  scaleY: number,
): DrawingPoint {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const sx = (point.x - basePoint.x) * scaleX;
  const sy = (point.y - basePoint.y) * scaleY;
  return {
    x: sx * cos - sy * sin + insertion.x,
    y: sx * sin + sy * cos + insertion.y,
  };
}

/**
 * round-6 finding 3 — the fully-placed ATTRIB text fields exported to a DXF
 * ATTRIB record, resolved AS A UNIT (group 10/20 position, group 40 height AND
 * group 50 rotation transformed together by the SAME INSERT affine). Splitting
 * them (round-4 transformed only the position while height/rotation kept the
 * untransformed ATTRIB/ATTDEF defaults) made a save → reimport of a value-only
 * attribute under a scaled/rotated INSERT return the label to the right point
 * but at the wrong size/angle — because the import parser bakes the position,
 * height AND rotation verbatim as absolute overrides (`dxfBridge.drawingToDxf.ts`).
 */
export interface ResolvedAttribExportGeometry {
  /** Group 10/20 — the placed insertion point. */
  position: DrawingPoint;
  /** Group 40 — the placed text height (ALWAYS emitted). */
  height: number;
  /** Group 50 — the placed rotation in degrees, or `undefined` to omit the
   *  group (default 0). */
  rotation: number | undefined;
  /**
   * Group 71 (text generation flags) — the placed handedness, or `undefined`
   * to omit the group (default 0). GitHub Codex review PR #558 round 2
   * (round-15 local read-only follow-up) P2 — "Export resolved flags for
   * block-local attributes". Resolved AS A UNIT with position/height/rotation
   * for the SAME reason finding 3 already resolves those three together: a
   * block-local (un-overridden) row's LIVE render/PDF/EXPLODE handedness
   * comes from the matching ATTDEF template's OWN `generationFlags`
   * (`attdefFromEntity.ts`, `insertAttributes.ts` — every consumer this PR
   * has touched across rounds 3/9/14), but `resolveAttribExportGeometry`
   * ALWAYS materialises an explicit absolute `position` for EVERY row
   * regardless of source regime — so on reimport, `dxfBridge.drawingToDxf.ts`
   * bakes that position as a genuine `overridePosition`, and the WORLD-
   * placement render/PDF branches read `overrideGenerationFlags ?? 0`
   * instead of the (now-detached) ATTDEF template — silently un-mirroring a
   * template-mirrored block-local attribute after one save/reimport
   * round-trip. Resolving (and exporting) the effective flags explicitly,
   * the SAME way height/rotation already are, keeps the round-trip
   * byte-faithful to the RENDERED result, not just the raw model shape.
   */
  generationFlags: number | undefined;
}

/**
 * round-6 finding 3 — the SCALAR angle-sum fold the render path applies to a
 * block-local child's rotation (`blockTransform.ts scalarRotationDeg`): a
 * DOUBLE-negative uniform scale `S(−s,−s) ≡ R(180°)` adds an extra 180°, a
 * single mirror keeps the raw sum (the legacy-pinned convention). Mirrors that
 * render truth EXACTLY so an unset-`overridePosition` ATTRIB's exported group
 * 50 equals the angle the scene actually draws.
 */
function scalarRotationDegForExport(
  rotationDeg: number,
  scaleX: number,
  scaleY: number,
): number {
  return scaleX < 0 && scaleY < 0 ? rotationDeg + 180 : rotationDeg;
}

/**
 * round-6 finding 1 — resolve the ATTDEF that a duplicate-tag ATTRIB row maps
 * to BY OCCURRENCE, the export-side twin of `attdefFromEntity.ts`'s render
 * `findMatchingAttribute` / `blockFromEntity.ts`'s `attdefTagOccurrenceCount`.
 * A block whose definition carries N same-tag VARIABLE ATTDEFs on distinct
 * anchors gets N same-tag ATTRIB rows on a real INSERT, matched positionally
 * (`ezdxf add_auto_attribs` walks the ATTDEFs in order). A plain `.find()`
 * returned the FIRST ATTDEF for EVERY row, so both rows resolved to the first
 * anchor and a reimport baked the second attribute into the wrong place.
 * CONSTANT ATTDEFs are skipped (they carry no per-instance ATTRIB row — mirrors
 * the render's `attdefTagOccurrenceCount` constant exclusion, round-5 F3), so a
 * constant sharing a tag never steals a variable occurrence's anchor.
 */
function findAttdefByTagOccurrence(
  blockDefinition: BlockDefinition,
  tag: string,
  occurrenceIndex: number,
): AttdefEntity | undefined {
  let seen = 0;
  for (const child of blockDefinition.entities) {
    if (child.type !== "Attdef" || child.tag !== tag) continue;
    if (child.flags.constant) continue;
    if (seen === occurrenceIndex) return child;
    seen++;
  }
  return undefined;
}

/**
 * round-4 FINDING 1 + round-6 findings 1/3/4 — resolve the ATTRIB's exported
 * DXF geometry (position + height + rotation, as a UNIT).
 *
 * An explicit `overridePosition` (a real EATTEDIT reposition, OR any imported
 * ATTRIB — the import parser ALWAYS populates this field verbatim from the raw
 * DXF group 10/20, dxfBridge.drawingToDxf.ts) wins unchanged — position,
 * height AND rotation are already the correct absolute placed values.
 *
 * When `overridePosition` is unset (round-3 FINDING 1+4 — a value-only
 * DDATTE/EATTEDIT edit that materialised a previously-absent ATTRIB row,
 * deliberately left un-seeded so the in-memory render keeps following the
 * ATTDEF template LIVE), the placed geometry is resolved by transforming the
 * matching ATTDEF's OWN template through the INSERT affine, EXACTLY as the
 * render path does (`attdefFromEntity.ts` → `transformBlockChild`):
 *   - position: the template anchor (`resolveAttdefTemplateAnchorForExport` —
 *     alignment point for a justified template, insertion point otherwise) run
 *     through `transformAttdefAnchorForExport` (empirically verified against
 *     `ezdxf add_auto_attribs`, which bakes the fully-transformed position);
 *   - height (finding 3): `attdef.height · |scaleY|` — the same `height ·
 *     heightScale` the render applies (`transformBlockChild` "Text");
 *   - rotation (finding 3): `attdef.rotation + scalarRotationDeg` — the same
 *     angle sum the render draws.
 * The matching ATTDEF is resolved BY OCCURRENCE (finding 1), not `.find()`.
 *
 * When NO ATTDEF template matches (an orphan ATTRIB whose ATTDEF was
 * removed/renamed after EATTEDIT), the render's `orphanAttributeFromEntity`
 * places the no-override fallback at the block origin `{0,0}` through the FULL
 * block transform — which SUBTRACTS the (possibly non-zero) block base point.
 * The export therefore transforms `{0,0}` through the SAME base point (finding
 * 4), not the bare INSERT insertion (which was correct only for a zero base
 * point), with height/rotation riding the affine too.
 */
export function resolveAttribExportGeometry(
  attr: BlockInsertAttribute,
  parent: BlockInsertEntity,
  blockDefinition: BlockDefinition | undefined,
  occurrenceIndex: number,
): ResolvedAttribExportGeometry {
  // round-15 — flags carry NO block-affine interaction (unlike
  // position/height/rotation, they're never transformed by INSERT
  // scale/rotation), just an override-vs-template precedence resolution;
  // shared by all three branches below. Divergence-gated the SAME way
  // `emitRotation` already omits a zero rotation — group 71 is optional,
  // default 0, so a resolved-to-0 handedness never invents the group.
  const emitFlags = (flags: number): number | undefined =>
    flags === 0 ? undefined : flags;

  // WORLD regime — already the placed absolute values (imported ATTRIB or a
  // real EATTEDIT reposition). Verbatim, no second transform (round-5 render
  // twin: `worldPlacement` rides the identity/outer residual).
  if (attr.overridePosition !== undefined) {
    return {
      position: attr.overridePosition,
      height: attr.overrideHeight ?? 2.5,
      rotation: attr.overrideRotation,
      // GitHub Codex review PR #558 (formal bot review, HEAD 4fe0a323) P2 —
      // "Preserve explicit zero ATTRIB generation flags". `attr
      // .overrideGenerationFlags` is `.optional()` (`model.ts`) — its OWN
      // presence, not `emitFlags`'s "collapse a RESOLVED value of 0"
      // convention, is already the correct byte-fidelity signal: an
      // imported ATTRIB that explicitly carried group 71 with value 0 must
      // export group 71 with value 0 again (not omit it), same as every
      // other presence-preserving field this PR established. The prior
      // `emitFlags(attr.overrideGenerationFlags ?? 0)` collapsed BOTH
      // "never had an override" AND "explicitly overridden to 0" to the
      // SAME `undefined` — indistinguishable, so a no-edit save of a
      // genuinely-zero-flags WORLD attribute silently dropped the group.
      // `emitFlags` stays correct at its OTHER 2 call sites below/above
      // (`matchingAttdef.generationFlags`, a REQUIRED/schema-defaulted
      // field where collapsing a RESOLVED 0 to "no group" is the right
      // behavior) — this is a presence-preserving passthrough of the raw
      // optional SOURCE field, not a resolved-value emit.
      generationFlags: attr.overrideGenerationFlags,
    };
  }

  const scalarRotationDeg = scalarRotationDegForExport(
    parent.rotation,
    parent.scaleX,
    parent.scaleY,
  );
  const heightScale = Math.abs(parent.scaleY);
  const emitRotation = (deg: number): number | undefined =>
    deg === 0 ? undefined : deg;

  const matchingAttdef = blockDefinition
    ? findAttdefByTagOccurrence(blockDefinition, attr.tag, occurrenceIndex)
    : undefined;

  if (!matchingAttdef || !blockDefinition) {
    // ORPHAN (finding 4) — transform the block-origin fallback {0,0} through
    // the SAME base point the render subtracts; height/rotation ride the affine
    // (the orphan render path scales height by |scaleY| and sums the rotation).
    // Flags carry no affine of their own — same override-only resolution as
    // the WORLD branch (no ATTDEF template to fall back to either).
    const basePoint = blockDefinition?.basePoint ?? { x: 0, y: 0 };
    return {
      position: transformAttdefAnchorForExport(
        { x: 0, y: 0 },
        basePoint,
        parent.insertionPoint,
        parent.rotation,
        parent.scaleX,
        parent.scaleY,
      ),
      height: (attr.overrideHeight ?? 2.5) * heightScale,
      rotation: emitRotation((attr.overrideRotation ?? 0) + scalarRotationDeg),
      // Same presence-preserving passthrough fix as the WORLD branch above
      // (GitHub Codex review PR #558, formal bot review HEAD 4fe0a323, P2)
      // — an orphan ATTRIB's own `overrideGenerationFlags` is the SAME
      // `.optional()` source field; an explicit 0 must round-trip as an
      // explicit 0, not collapse to "no group" alongside a genuinely-absent
      // override.
      generationFlags: attr.overrideGenerationFlags,
    };
  }

  const templateAnchor = resolveAttdefTemplateAnchorForExport(matchingAttdef);
  return {
    position: transformAttdefAnchorForExport(
      templateAnchor,
      blockDefinition.basePoint,
      parent.insertionPoint,
      parent.rotation,
      parent.scaleX,
      parent.scaleY,
    ),
    // finding 3 — the seeded override, when present, IS the template value
    // (`attributeMutations.ts`: `overrideHeight ?? matchingAttdef.height`), so
    // `?? matchingAttdef.*` resolves identically whether or not it was seeded.
    height: (attr.overrideHeight ?? matchingAttdef.height) * heightScale,
    rotation: emitRotation(
      (attr.overrideRotation ?? matchingAttdef.rotation) + scalarRotationDeg,
    ),
    // round-15 GitHub Codex bot P2, corrected by the round-16 local
    // read-only follow-up P2 — "Use template flags for block-local
    // attributes". UNLIKE height/rotation (whose `attr.overrideHeight ??
    // matchingAttdef.height` fallback is safe ONLY because
    // `attributeMutations.ts` SEEDS `overrideHeight` to the template value
    // — finding 3's own comment above), generationFlags carries NO such
    // seeding invariant: a block-local row CAN genuinely carry a STRAY
    // `overrideGenerationFlags` that DIFFERS from its ATTDEF (round-14's
    // own regression test constructs exactly this case). The render's own
    // established rule (`attdefFromEntity.ts`'s block-local branch) is
    // UNCONDITIONAL — `attdef.generationFlags`, with NO `attr.overrideGener
    // ationFlags ??` fallback at all — "out of MIRRTEXT's scope" for a
    // block-local row regardless of what the row itself carries. The
    // FIRST round-15 draft used `attr.overrideGenerationFlags ??
    // matchingAttdef.generationFlags`, which gave the row's stray value
    // priority when present — the exact opposite of the render rule, so a
    // block-local row with a differing stray override would visibly
    // change handedness across a save/reload (template 4 + stray row 0 →
    // wrongly exports/reimports as unmirrored).
    //
    // GitHub Codex review PR #558 round 39 (formal bot review, HEAD
    // 6bf071e6) P2 — "Fold INSERT mirror signs into exported ATTRIB
    // flags" — investigated and DEFERRED (not a same-scope fix), tracked
    // as https://github.com/cosc/Edit-dwg/issues/566. The finding's DXF
    // semantics are correct (ezdxf `Text.transform` DOES fold a block's
    // own reflection into a child TEXT/ATTRIB's BACKWARD/UPSIDE_DOWN
    // flags) but its premise — "visually unflipped before saving, the two
    // reflections cancel" — does NOT match this codebase's CURRENT,
    // deliberately-scoped render: `blockTransform.ts`'s own module header
    // ("MIRRORED TEXT decision") and the golden-parity test
    // `block-flatten-superiority.test.ts` ("F6 mirror decision") both
    // explicitly document/lock "glyphs are NOT mirrored... no group-71
    // BACKWARD/UPSIDE_DOWN toggle" for a negative-scale INSERT as the
    // current, TESTED contract — the full ezdxf-correct behavior is
    // named/tracked as the coupled "S10-XFORM" slice, deliberately NOT
    // done yet because "flipping it here would break the golden-parity-
    // vs-legacy precondition the S11 cutover rests on" (that module's own
    // words). Folding the scale sign into ONLY this export encoder (while
    // the WebGL render + PDF plot paths both still emit unfolded flags)
    // would swap today's consistent (backward-before, backward-after)
    // behavior for a NEW inconsistent one (backward-before, NOT-backward-
    // after) — the opposite-direction bug, not a fix. See issue #566 for
    // the full three-call-site (render + PDF + export) fix this needs.
    generationFlags: emitFlags(matchingAttdef.generationFlags),
  };
}

/**
 * Build a `DxfAttribEntity` for an INSERT attribute. Co-located with its
 * placement resolver (`resolveAttribExportGeometry`) — moved here from
 * `dxfBridge.entityConversion.ts` (which is pinned at the 2000-line guardrail)
 * following the sibling-encoder precedent (`dxfBridge.ellipse.ts`,
 * `dxfBridge.spline.ts`). To stay off the `entityConversion` import cycle
 * (`toDxfPoint`/`toDxfCommonProps` live there, and this module is imported BY
 * it), the caller passes the already-built `baseCommon` (its
 * `toDxfCommonProps(space)`) and this encoder inlines the trivial group-10
 * point map (`{ x, y, z: 0 }`, identical to `toDxfPoint`).
 *
 * The attribute mirrors the position / style / rotation of the parent INSERT
 * unless explicit overrides were captured during import or a later EATTEDIT
 * action (round-6 findings 1/3/4 — resolved as a UNIT in
 * `resolveAttribExportGeometry`).
 */
export function drawingAttribToDxfAttrib(
  attr: BlockInsertAttribute,
  parent: BlockInsertEntity,
  baseCommon: DxfAttribEntity["common"],
  options?: {
    // FINDING 4 — stamp `common.forceLayerEmit` so the ATTRIB emits group 8 on
    // EVERY record (incl layer "0"), matching its parent INSERT on the
    // from-scratch export; omitted on carry-forward (byte-identical omit-8-when-0).
    forceLayerEmit?: boolean;
    blockDefinition?: BlockDefinition; // round-4 F1 — unset overridePosition falls back to the ATTDEF anchor.
    // round-6 finding 1 — this row's 0-based occurrence among same-tag rows
    // (`attributes[]` order); selects the matching-by-position ATTDEF anchor for
    // a duplicate-tag block (default 0 = byte-identical unique-tag first-match).
    occurrenceIndex?: number;
  },
): DxfAttribEntity {
  const placed = resolveAttribExportGeometry(
    attr,
    parent,
    options?.blockDefinition,
    options?.occurrenceIndex ?? 0,
  );
  const flags = attr.invisible ? 1 : 0;
  const common: DxfAttribEntity["common"] = {
    ...baseCommon,
    layer: parent.layer,
  };
  if (attr.handle) common.handle = attr.handle;
  if (options?.forceLayerEmit) common.forceLayerEmit = true;
  return {
    kind: "supported-entity",
    type: "ATTRIB",
    common,
    subclassMarkers: [],
    extraGroups: [],
    geometry: {
      insertionPoint: { x: placed.position.x, y: placed.position.y, z: 0 },
      height: placed.height,
      value: attr.value,
      tag: attr.tag,
      flags,
      flagBits: {
        invisible: attr.invisible === true,
        constant: false,
        verify: false,
        preset: false,
        lockPosition: false,
        multiline: false,
      },
      ...(placed.rotation !== undefined ? { rotation: placed.rotation } : {}),
      ...(attr.overrideStyle ? { styleName: attr.overrideStyle } : {}),
      // GitHub Codex review PR #558 (post-rebase fresh pass) P2, corrected by
      // the round-15 local read-only follow-up P2 — "Export resolved flags
      // for block-local attributes". DXF group 71 (text generation flags),
      // the handedness companion to `rotation` above. `placed.generationFlags`
      // now resolves the EFFECTIVE handedness AS A UNIT with position/height/
      // rotation (`resolveAttribExportGeometry`'s own doc) — a block-local
      // row's OWN row carries no override, so this falls back to the
      // matching ATTDEF's template flags (the SAME fallback the render/PDF
      // paths already use), rather than the PREVIOUS `attr.overrideGeneration
      // Flags !== undefined`-only check, which silently dropped a
      // template-inherited mirror/backward state on export — un-mirroring
      // the attribute the moment the file round-trips through save/reimport
      // (which always bakes an explicit `overridePosition`, switching the
      // reimported row onto the WORLD-regime `?? 0` default instead of the
      // template).
      ...(placed.generationFlags !== undefined
        ? { generationFlags: placed.generationFlags }
        : {}),
    },
  };
}
